/**
 * theme.js — Modo claro/oscuro/personalizado + personalización de colores.
 * Persiste en localStorage. Se aplica antes del render para evitar parpadeo.
 */
// Ver la nota de `utils.js`: `window.X`, no `const X`.
window.Theme = (() => {
  const KEY = 'intranet_theme';
  const DEFAULTS = { mode: 'light', primary: '#0D2B4E', accent: '#F5A623', density: 'comfortable' };

  // Presets de marca (primary, accent)
  const PRESETS = [
    { name: 'Su Servicio',  primary: '#0D2B4E', accent: '#F5A623' },
    { name: 'Esmeralda',    primary: '#0B3D2E', accent: '#2ECC71' },
    { name: 'Vino',         primary: '#4A0E1E', accent: '#E74C3C' },
    { name: 'Índigo',       primary: '#1E1B4B', accent: '#818CF8' },
    { name: 'Océano',       primary: '#0C4A6E', accent: '#38BDF8' },
    { name: 'Ciruela',      primary: '#3B0764', accent: '#C084FC' },
    { name: 'Carbón',       primary: '#1F2937', accent: '#F59E0B' },
  ];

  function _load() {
    try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || '{}') }; }
    catch { return { ...DEFAULTS }; }
  }
  // La escritura, protegida igual que la lectura de arriba. Aquí NO se usa
  // `Utils.recordar`, aunque hace justo esto: `theme.js` se carga antes que
  // `utils.js` en el índice —y corre `Theme.init()` al cargarse, para aplicar
  // el tema antes del primer pintado y que no parpadee—, así que `Utils` aún
  // no existe. Depender de él aquí sería una mina que solo estalla el día que
  // alguien llame a `_save()` un poco antes.
  //
  // Un tema que no se persiste sigue aplicándose en esta pestaña: se pierde la
  // comodidad, no la elección.
  function _save(cfg) {
    try { localStorage.setItem(KEY, JSON.stringify(cfg)); } catch {}
  }

  /** Oscurece/aclara un hex un porcentaje (-1..1). */
  function _shade(hex, pct) {
    const n = parseInt(hex.replace('#', ''), 16);
    let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    const t = pct < 0 ? 0 : 255, p = Math.abs(pct);
    r = Math.round((t - r) * p) + r;
    g = Math.round((t - g) * p) + g;
    b = Math.round((t - b) * p) + b;
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  /** Luminancia relativa de un hex, para medir contraste. */
  function _luz(hex) {
    const n = parseInt(hex.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
      .map(v => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); })
      .reduce((a, c, i) => a + c * [0.2126, 0.7152, 0.0722][i], 0);
  }

  function _contraste(a, b) {
    const [l1, l2] = [_luz(a), _luz(b)].sort((x, y) => y - x);
    return (l1 + 0.05) / (l2 + 0.05);
  }

  /**
   * La tinta con la que se ESCRIBE el acento sobre una superficie.
   *
   * `--orange-text` existe porque el acento vivo no se lee sobre blanco: el de
   * fábrica (#F5A623) da 1,9. Pero `setColors` solo cambiaba `--orange`, así que
   * al elegir otro acento el texto seguía saliendo del naranja de fábrica —verde
   * en las superficies y marrón en las letras—. Medido el 2026-09-17 con el
   * preset «Vino»: la etiqueta «Fijado» del Inicio quedó en 2,89.
   *
   * Se oscurece (o se aclara, en modo oscuro) hasta pasar de 4,5 sobre la
   * superficie de una tarjeta, que es donde casi siempre se pinta.
   */
  function _tintaDelAcento(hex, oscuro) {
    const fondo = oscuro ? '#1e293b' : '#ffffff';
    let mejor = hex;
    for (let paso = 0; paso <= 12; paso++) {
      mejor = _shade(hex, (oscuro ? 0.06 : -0.06) * paso);
      if (_contraste(mejor, fondo) >= 4.5) break;
    }
    return mejor;
  }

  /** Dos hex mezclados: el segundo con el peso `pct` (0..1). */
  function _mezcla(hex1, hex2, pct) {
    const a = parseInt(hex1.replace('#', ''), 16), b = parseInt(hex2.replace('#', ''), 16);
    const canal = (desp) => Math.round((((a >> desp) & 255) * (1 - pct)) + (((b >> desp) & 255) * pct));
    return '#' + ((1 << 24) + (canal(16) << 16) + (canal(8) << 8) + canal(0)).toString(16).slice(1);
  }

  /**
   * El acento cuando se ESCRIBE encima de la marca.
   *
   * El acento de fábrica se lee sobre el navy (4,92 con el velo blanco del 12 %
   * de la tarjeta del evento fijado), y por eso ahí se usa sin oscurecer. Con
   * otra marca deja de valer: medido el 2026-09-17, cinco de los siete temas
   * predefinidos quedaban por debajo de 4,5 y «Vino» daba 2,89.
   *
   * Se aclara el acento hasta pasar de 4,5 sobre esa superficie. Si ni el blanco
   * llega —no debería—, se queda en el último valor probado.
   */
  function _acentoSobreMarca(acento, marca) {
    const fondo = _mezcla(marca, '#ffffff', 0.12);
    let mejor = acento;
    for (let paso = 0; paso <= 14; paso++) {
      mejor = _shade(acento, 0.06 * paso);
      if (_contraste(mejor, fondo) >= 4.5) break;
    }
    return mejor;
  }

  /** Aplica la configuración actual al documento. */
  function apply(cfg = _load()) {
    const root = document.documentElement;

    // Modo (claro/oscuro)
    if (cfg.mode === 'dark') root.setAttribute('data-theme', 'dark');
    else root.removeAttribute('data-theme');

    // Colores personalizados (solo cuando NO es oscuro, para no romper la legibilidad
    // del remapeo de --navy en modo oscuro). En oscuro solo personalizamos el acento.
    // `--orange-text` acompaña SIEMPRE al acento elegido: es la tinta con la que
    // se escribe, y dejarla en la de fábrica mezclaba dos marcas en la pantalla.
    if (cfg.mode === 'custom') {
      root.style.setProperty('--navy', cfg.primary);
      root.style.setProperty('--navy-light', _shade(cfg.primary, 0.25));
      root.style.setProperty('--navy-dark', _shade(cfg.primary, -0.3));
      root.style.setProperty('--brand', cfg.primary);
      root.style.setProperty('--orange', cfg.accent);
      root.style.setProperty('--orange-dark', _shade(cfg.accent, -0.2));
      root.style.setProperty('--orange-text', _tintaDelAcento(cfg.accent, false));
      root.style.setProperty('--orange-sobre-marca', _acentoSobreMarca(cfg.accent, cfg.primary));
    } else {
      // Limpiar overrides inline (vuelve a los valores del CSS / dark)
      ['--navy', '--navy-light', '--navy-dark', '--brand', '--orange', '--orange-dark',
       '--orange-text', '--orange-sobre-marca']
        .forEach(v => root.style.removeProperty(v));
      // En claro u oscuro, permitir un acento personalizado opcional
      if (cfg.accent && cfg.accent !== DEFAULTS.accent) {
        root.style.setProperty('--orange', cfg.accent);
        root.style.setProperty('--orange-dark', _shade(cfg.accent, -0.2));
        root.style.setProperty('--orange-text', _tintaDelAcento(cfg.accent, cfg.mode === 'dark'));
        // La marca sigue siendo la de fábrica: `--brand` no se invierte en oscuro.
        root.style.setProperty('--orange-sobre-marca', _acentoSobreMarca(cfg.accent, DEFAULTS.primary));
      }
    }

    // Densidad (cómodo / compacto)
    document.body.classList.toggle('density-compact', cfg.density === 'compact');
  }

  function setDensity(d) {
    const cfg = _load(); cfg.density = d; _save(cfg); apply(cfg);
  }

  /** Init temprano (se llama en el boot, antes del login). */
  function init() { apply(); }

  function setMode(mode) {
    const cfg = _load();
    cfg.mode = mode;
    _save(cfg);
    apply(cfg);
  }

  function setColors(primary, accent) {
    const cfg = _load();
    if (primary) cfg.primary = primary;
    if (accent)  cfg.accent = accent;
    // Si personaliza colores, pasa a modo "custom" (salvo que esté en oscuro)
    if (cfg.mode !== 'dark') cfg.mode = 'custom';
    _save(cfg);
    apply(cfg);
  }

  function reset() {
    _save({ ...DEFAULTS });
    apply(DEFAULTS);
  }

  // ── Panel de configuración ────────────────────────────────────────
  function openPanel() {
    // Cerrar si ya está abierto
    const existing = document.getElementById('theme-panel');
    if (existing) { existing.remove(); return; }

    const cfg = _load();
    const panel = document.createElement('div');
    panel.className = 'theme-panel';
    panel.id = 'theme-panel';
    panel.innerHTML = `
      <div class="theme-panel-title"><i class="bi bi-palette"></i> Apariencia</div>

      <div class="theme-mode-grid">
        <div class="theme-mode-btn ${cfg.mode==='light'?'active':''}" data-mode="light">
          <i class="bi bi-sun"></i> Claro
        </div>
        <div class="theme-mode-btn ${cfg.mode==='dark'?'active':''}" data-mode="dark">
          <i class="bi bi-moon-stars"></i> Oscuro
        </div>
        <div class="theme-mode-btn ${cfg.mode==='custom'?'active':''}" data-mode="custom">
          <i class="bi bi-brush"></i> Personalizado
        </div>
      </div>

      <div class="theme-color-row">
        <label><i class="bi bi-circle-fill" style="color:var(--navy)"></i> Color primario</label>
        <input type="color" id="theme-primary" value="${cfg.primary}" />
      </div>
      <div class="theme-color-row">
        <label><i class="bi bi-circle-fill" style="color: var(--orange)"></i> Color de acento</label>
        <input type="color" id="theme-accent" value="${cfg.accent}" />
      </div>

      <div style="font-size:var(--fs-2);color: var(--text-muted);margin:.5rem 0 .3rem">Temas predefinidos:</div>
      <div class="theme-presets" id="theme-presets">
        ${PRESETS.map(p => `
          <div class="theme-preset" title="${Utils.escapeHtml(p.name)}"
               data-primary="${p.primary}" data-accent="${p.accent}"
               style="background:linear-gradient(135deg, ${p.primary} 50%, ${p.accent} 50%)"></div>
        `).join('')}
      </div>

      <div style="font-size:var(--fs-2);color: var(--text-muted);margin:.75rem 0 .3rem">Densidad:</div>
      <div class="theme-mode-grid" style="grid-template-columns:1fr 1fr">
        <div class="theme-mode-btn ${cfg.density!=='compact'?'active':''}" data-density="comfortable">
          <i class="bi bi-distribute-vertical"></i> Cómodo
        </div>
        <div class="theme-mode-btn ${cfg.density==='compact'?'active':''}" data-density="compact">
          <i class="bi bi-list"></i> Compacto
        </div>
      </div>

      <button class="btn btn-ghost btn-sm btn-block" id="theme-reset" style="margin-top:.5rem">
        <i class="bi bi-arrow-counterclockwise"></i> Restaurar predeterminado
      </button>`;

    document.body.appendChild(panel);

    // Densidad
    panel.querySelectorAll('.theme-mode-btn[data-density]').forEach(btn => {
      btn.addEventListener('click', () => {
        setDensity(btn.dataset.density);
        panel.querySelectorAll('.theme-mode-btn[data-density]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });

    // Modos
    panel.querySelectorAll('.theme-mode-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        setMode(btn.dataset.mode);
        panel.querySelectorAll('.theme-mode-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });

    // Color pickers
    panel.querySelector('#theme-primary').addEventListener('input', e =>
      setColors(e.target.value, null));
    panel.querySelector('#theme-accent').addEventListener('input', e =>
      setColors(null, e.target.value));

    // Presets
    panel.querySelectorAll('.theme-preset').forEach(pr => {
      pr.addEventListener('click', () => {
        setColors(pr.dataset.primary, pr.dataset.accent);
        panel.querySelector('#theme-primary').value = pr.dataset.primary;
        panel.querySelector('#theme-accent').value = pr.dataset.accent;
        panel.querySelectorAll('.theme-mode-btn').forEach(b =>
          b.classList.toggle('active', b.dataset.mode === 'custom'));
      });
    });

    // Reset
    panel.querySelector('#theme-reset').addEventListener('click', () => {
      reset();
      panel.remove();
    });

    // Cerrar al hacer clic fuera
    setTimeout(() => {
      document.addEventListener('click', function close(e) {
        if (!e.target.closest('#theme-panel') && !e.target.closest('#theme-btn')) {
          panel.remove();
          document.removeEventListener('click', close);
        }
      });
    }, 50);
  }

  // `shade` se exponía para pintar como TEXTO un color configurable desde la BD
  // y aclararlo en modo oscuro. Se retiró el 2026-09-17: su único consumidor, el
  // calendario vanilla, preguntaba por `window.Theme` —que no existe, porque
  // `Theme` es un `const`— y nunca llegaba a llamarla. La capa React no pinta
  // esos colores como texto: los usa de marca (`features/eventos/piezas.tsx`).
  // `porDefecto` se expone porque `app.js` necesita saber cuál es la marca de
  // fábrica para distinguirla de una personalizada. Antes llevaba el `#0D2B4E`
  // copiado, así que cambiar la paleta por defecto aquí dejaba aquella
  // comparación mirando un color que ya no existía — sin dar ningún error.
  return { init, openPanel,
           porDefecto: { ...DEFAULTS } };
})();


// Aplicar lo antes posible para evitar parpadeo
Theme.init();
